import { z } from 'zod';
import { useEffect } from 'react';
import {
    CommanderFormProps,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, lt } from '@mews/framework';
import { SUPPORTED_FILE_EXTENSIONS } from '../../../../common/constants';
import { uploadFileSchema } from '../../schema';

export type UploadFileFormValues = z.infer<ReturnType<typeof uploadFileSchema>>;

const uploadFileFormDefinition = (): SchemaFormDefinition<ReturnType<typeof uploadFileSchema>> => ({
    fields: {
        file: {
            label: lt('File'),
            field: { type: 'file', accept: SUPPORTED_FILE_EXTENSIONS },
            required: true,
        },
        name: {
            label: lt('ContractsAttachmentLibraryCustomFileName'),
        },
    },
});

type UploadAttachmentFormProps = CommanderFormProps & {
    addAttachment: (values: UploadFileFormValues) => void;
    existingNames: string[];
};

export const UploadAttachmentForm = ({ addAttachment, existingNames, ...props }: UploadAttachmentFormProps) => {
    const schema = uploadFileSchema(existingNames);

    const { renderFormContent, watch, setValue } = useHandledDialogFormControls<UploadFileFormValues>({
        ...props,
        defaultValues: { file: [], name: '' },
        resolver: createZodFormResolver(schema),
        onSubmit: (values) => addAttachment(values),
    });

    const file = watch('file');
    const fileName = file?.[0]?.name;

    useEffect(() => {
        if (fileName != null) {
            setValue('name', fileName);
        }
    }, [fileName, setValue]);

    return renderFormContent(<SchemaFormFields schema={schema} formDefinition={uploadFileFormDefinition()} />);
};
